<script>
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxSlaPoliciesAPI from 'dashboard/api/inboxSlaPolicies';

const PRIORITIES = [
  { key: 'low', value: 0, color: 'text-green-600' },
  { key: 'medium', value: 1, color: 'text-yellow-600' },
  { key: 'high', value: 2, color: 'text-orange-600' },
  { key: 'urgent', value: 3, color: 'text-red-600' },
];

export default {
  components: {
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      policies: {},
    };
  },
  computed: {
    priorities() {
      return PRIORITIES.map(p => ({
        ...p,
        label: this.$t(`CONVERSATION.PRIORITY.OPTIONS.${p.key.toUpperCase()}`),
      }));
    },
  },
  watch: {
    'inbox.id': {
      handler(newId) {
        if (newId) this.fetchPolicies();
      },
      immediate: true,
    },
    policies: {
      deep: true,
      handler() {
        PRIORITIES.forEach(p => {
          if (!this.anyFieldFilled(p.value)) {
            this.policies[p.value].active = false;
          }
        });
      },
    },
  },
  methods: {
    anyFieldFilled(priorityValue) {
      const policy = this.policies[priorityValue];
      return (
        !!policy.first_response_time ||
        !!policy.every_response_time ||
        !!policy.resolution_time
      );
    },
    initEmptyPolicies() {
      const policies = {};
      PRIORITIES.forEach(p => {
        policies[p.value] = {
          id: null,
          active: false,
          first_response_time: '',
          every_response_time: '',
          resolution_time: '',
        };
      });
      return policies;
    },
    formatMinutes(minutes) {
      if (!minutes && minutes !== 0) return '';
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      if (h > 0 && m > 0) return `${h}h ${m}m`;
      if (h > 0) return `${h}h`;
      if (m > 0) return `${m}m`;
      return '';
    },
    async fetchPolicies() {
      if (!this.inbox.id) return;
      this.isLoading = true;
      this.policies = this.initEmptyPolicies();
      try {
        const { data } = await InboxSlaPoliciesAPI.get(this.inbox.id);
        data.forEach(policy => {
          this.policies[policy.priority] = {
            id: policy.id,
            active: policy.active,
            first_response_time: this.formatMinutes(policy.first_response_time),
            every_response_time: this.formatMinutes(policy.every_response_time),
            resolution_time: this.formatMinutes(policy.resolution_time),
          };
        });
      } catch {
        // no policies yet, use empty defaults
      }
      this.isLoading = false;
    },
    parseDuration(value) {
      if (typeof value === 'number') return value;
      const str = (value || '').toString().trim();
      if (!str) return null;

      let totalMinutes = 0;
      const hours = str.match(/(\d+)\s*h/i);
      const minutes = str.match(/(\d+)\s*m/i);

      if (hours) totalMinutes += parseInt(hours[1], 10) * 60;
      if (minutes) totalMinutes += parseInt(minutes[1], 10);
      return totalMinutes;
    },
    async savePolicies() {
      this.isSaving = true;
      try {
        const promises = PRIORITIES.map(priority => {
          const policy = this.policies[priority.value];
          const payload = {
            priority: priority.value,
            active: policy.active,
            first_response_time: this.parseDuration(policy.first_response_time),
            every_response_time: this.parseDuration(policy.every_response_time),
            resolution_time: this.parseDuration(policy.resolution_time),
          };

          if (policy.id) {
            return InboxSlaPoliciesAPI.update(
              this.inbox.id,
              policy.id,
              payload
            );
          }
          const hasValues =
            policy.first_response_time ||
            policy.every_response_time ||
            policy.resolution_time;
          if (hasValues || policy.active) {
            return InboxSlaPoliciesAPI.create(this.inbox.id, payload).then(
              ({ data }) => {
                policy.id = data.id;
              }
            );
          }
          return Promise.resolve();
        });
        await Promise.all(promises);
        useAlert(this.$t('INBOX_MGMT.SLA.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.SLA.API.ERROR_MESSAGE'));
      }
      this.isSaving = false;
    },
  },
};
</script>

<template>
  <div>
    <SettingsSection
      :title="$t('INBOX_MGMT.SLA.TITLE')"
      :sub-title="$t('INBOX_MGMT.SLA.SUB_TEXT')"
    >
      <div v-if="isLoading" class="text-sm text-slate-500">
        {{ $t('INBOX_MGMT.SLA.LOADING') }}
      </div>
      <table v-else class="sla-table w-full">
        <thead>
          <tr>
            <th
              class="text-left text-sm font-medium text-slate-600 dark:text-slate-400 pb-3 pr-4"
            >
              {{ $t('INBOX_MGMT.SLA.FIELDS.ACTIVE') }}
            </th>
            <th
              class="text-left text-sm font-medium text-slate-600 dark:text-slate-400 pb-3 pr-4"
            >
              {{ $t('INBOX_MGMT.SLA.FIELDS.TICKET_PRIORITY') }}
            </th>
            <th
              class="text-left text-sm font-medium text-slate-600 dark:text-slate-400 pb-3 px-2"
            >
              {{ $t('INBOX_MGMT.SLA.FIELDS.FIRST_RESPONSE_TIME') }}
            </th>
            <th
              class="text-left text-sm font-medium text-slate-600 dark:text-slate-400 pb-3 px-2"
            >
              {{ $t('INBOX_MGMT.SLA.FIELDS.EVERY_RESPONSE_TIME') }}
            </th>
            <th
              class="text-left text-sm font-medium text-slate-600 dark:text-slate-400 pb-3 px-2"
            >
              {{ $t('INBOX_MGMT.SLA.FIELDS.RESOLUTION_TIME') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="priority in priorities"
            :key="priority.key"
            class="border-t border-slate-200 dark:border-slate-700"
          >
            <td class="py-3 pr-4">
              <input
                :id="`sla-active-${priority.key}`"
                v-model="policies[priority.value].active"
                type="checkbox"
                :disabled="!anyFieldFilled(priority.value)"
              />
            </td>
            <td class="py-3 pr-4">
              <label
                :for="`sla-active-${priority.key}`"
                class="font-medium whitespace-nowrap"
                :class="priority.color"
              >
                {{ priority.label }}
              </label>
            </td>
            <td class="py-3 px-2">
              <input
                v-model="policies[priority.value].first_response_time"
                type="text"
                class="w-full !mb-0"
                :placeholder="$t('INBOX_MGMT.SLA.FIELDS.PLACEHOLDER')"
              />
            </td>
            <td class="py-3 px-2">
              <input
                v-model="policies[priority.value].every_response_time"
                type="text"
                class="w-full !mb-0"
                :placeholder="$t('INBOX_MGMT.SLA.FIELDS.PLACEHOLDER')"
              />
            </td>
            <td class="py-3 px-2">
              <input
                v-model="policies[priority.value].resolution_time"
                type="text"
                class="w-full !mb-0"
                :placeholder="$t('INBOX_MGMT.SLA.FIELDS.PLACEHOLDER')"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="mt-6">
        <NextButton
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :is-loading="isSaving"
          @click="savePolicies"
        />
      </div>
    </SettingsSection>
  </div>
</template>
